import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@clerk/clerk-react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { BACKEND_URL } from "@/constants/api";
import { Button } from "@/components/ui/button";
import {
  Users,
  ChevronLeft,
  Copy,
  Check,
  AlertCircle,
  ShieldCheck,
  UserX,
  UserCheck,
  Trash2,
} from "lucide-react";

export default function SchoolMembers() {
  const { schoolId } = useParams();
  const { getToken } = useAuth();

  const [school, setSchool] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [actionInProgress, setActionInProgress] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const token = await getToken();
      const headers = { Authorization: `Bearer ${token}` };
      const [schoolRes, membersRes] = await Promise.all([
        fetch(`${BACKEND_URL}api/schools/${schoolId}`, { headers }),
        fetch(`${BACKEND_URL}api/schools/${schoolId}/members`, { headers }),
      ]);
      if (!schoolRes.ok || !membersRes.ok) throw new Error("Failed to load members");
      setSchool(await schoolRes.json());
      setMembers(await membersRes.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [schoolId, getToken]);

  useEffect(() => {
    load();
  }, [load]);

  const copyInviteCode = () => {
    if (!school?.inviteCode) return;
    navigator.clipboard.writeText(school.inviteCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const regenerateCode = async () => {
    if (!confirm("Regenerate invite code? The old code will stop working.")) return;
    setRegenerating(true);
    try {
      const token = await getToken();
      const res = await fetch(
        `${BACKEND_URL}api/schools/${schoolId}/regenerate-code`,
        { method: "POST", headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error("Failed to regenerate code");
      const { inviteCode } = await res.json();
      setSchool((prev) => ({ ...prev, inviteCode }));
      toast.success("Invite code regenerated.");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setRegenerating(false);
    }
  };

  const setStatus = async (userId, status) => {
    setActionInProgress(userId + status);
    try {
      const token = await getToken();
      const res = await fetch(
        `${BACKEND_URL}api/schools/${schoolId}/members/${userId}/status`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ status }),
        }
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Failed to update status");
      }
      setMembers((prev) =>
        prev.map((m) => (String(m.userId) === userId ? { ...m, status } : m))
      );
      toast.success(status === "disabled" ? "Member disabled." : "Member enabled.");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setActionInProgress(null);
    }
  };

  const removeMember = async (userId, name) => {
    if (!confirm(`Remove ${name} from this school?`)) return;
    setActionInProgress(userId + "remove");
    try {
      const token = await getToken();
      const res = await fetch(
        `${BACKEND_URL}api/schools/${schoolId}/members/${userId}`,
        { method: "DELETE", headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Failed to remove member");
      }
      setMembers((prev) => prev.filter((m) => m.userId.toString() !== userId));
      toast.success("Member removed.");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setActionInProgress(null);
    }
  };

  if (loading) {
    return (
      <div className="dashboard-content">
        <div className="h-8 w-40 bg-muted animate-pulse rounded mb-6" />
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-14 bg-muted animate-pulse rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-content">
        <div className="flex items-center gap-2 text-destructive text-sm rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3">
          <AlertCircle size={15} />
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-content">
      <Link
        to={`/schools/${schoolId}`}
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6"
      >
        <ChevronLeft size={14} />
        {school?.name}
      </Link>

      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
            <Users size={18} className="text-primary" />
          </div>
          <div>
            <h1 className="dashboard-heading-sm">Members</h1>
            <p className="dashboard-subheading">
              {members.length} member{members.length !== 1 ? "s" : ""}
            </p>
          </div>
        </div>
      </div>

      {/* Invite code card */}
      <div className="bg-card border border-border rounded-xl px-5 py-4 mb-6">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
          Invite Code
        </p>
        <div className="flex items-center gap-2 flex-wrap">
          <code className="text-base font-mono tracking-widest bg-muted px-3 py-1.5 rounded-lg select-all">
            {school?.inviteCode}
          </code>
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5 h-8"
            onClick={copyInviteCode}
          >
            {copied ? <Check size={13} /> : <Copy size={13} />}
            {copied ? "Copied" : "Copy"}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 text-muted-foreground hover:text-foreground text-xs"
            onClick={regenerateCode}
            disabled={regenerating}
          >
            {regenerating ? "Regenerating..." : "Regenerate"}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          Share this code with teachers to let them join.
        </p>
      </div>

      {/* Members list */}
      <div className="space-y-2">
        {members.map((member) => {
          const uid = member.userId?.toString?.() ?? member.userId;
          const busy = actionInProgress?.startsWith(uid);
          return (
            <div
              key={uid}
              className="flex items-center justify-between gap-3 bg-card border border-border rounded-xl px-4 py-3"
            >
              <div className="flex items-center gap-3 min-w-0">
                {member.isAdmin && (
                  <ShieldCheck size={15} className="text-primary shrink-0" />
                )}
                <div className="min-w-0">
                  <div className="text-sm font-medium text-foreground truncate">
                    {member.name}
                    {member.isAdmin && (
                      <span className="ml-2 text-xs text-primary font-normal">
                        Admin
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">
                    {member.email}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                <span
                  className={`text-xs px-2 py-0.5 rounded-full ${
                    member.status === "active"
                      ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                      : "bg-muted text-muted-foreground"
                  }`}
                >
                  {member.status}
                </span>

                {!member.isAdmin && (
                  <div className="flex gap-1">
                    {member.status === "active" ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0 text-muted-foreground hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20"
                        title="Disable member"
                        disabled={busy}
                        onClick={() => setStatus(uid, "disabled")}
                      >
                        <UserX size={14} />
                      </Button>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0 text-muted-foreground hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20"
                        title="Enable member"
                        disabled={busy}
                        onClick={() => setStatus(uid, "active")}
                      >
                        <UserCheck size={14} />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                      title="Remove member"
                      disabled={busy}
                      onClick={() => removeMember(uid, member.name)}
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
